import type { ConnectionState } from './types.js';

/** Xero kills a refresh token after 60 days without use. */
export const REFRESH_TOKEN_WINDOW_DAYS = 60;

/** Application Insights raises an alert once the remaining window hits this. */
export const EXPIRY_WARNING_DAYS = 14;

/**
 * Has Xero stopped accepting calls for this organisation altogether?
 *
 * THE FAILURE THIS EXISTS FOR
 * A client can remove this application's access to their organisation from
 * inside Xero. When they do, the refresh token keeps working — it belongs to
 * the authorisation, not the organisation — so every renewal succeeds and
 * every health check based on renewals says the connection is fine. Meanwhile
 * every actual request comes back refused.
 *
 * That happened here and went unnoticed for a week: sixty-three refusals
 * against an organisation the system was still reporting as healthy, with no
 * recorded failure and no error. Anybody looking at it would have seen a green
 * badge above an empty screen.
 *
 * Xero says 'AuthenticationUnsuccessful' in the body when the connection is
 * the problem. A 403 without it is something narrower — a scope this call
 * needed and the grant does not cover — which does not mean the connection is
 * gone and must not be treated as though it were, or one unsupported endpoint
 * would declare a working client disconnected.
 */
export function isConnectionGone(status: number, body: string): boolean {
  // The token itself was rejected.
  if (status === 401) return true;
  // The token is fine; this organisation is no longer reachable with it.
  if (status === 403) return body.includes('AuthenticationUnsuccessful');
  return false;
}

export interface ConnectionHealthInput {
  /** Days since the refresh token was last rotated. */
  ageDays: number;
  /** Whether the most recent refresh attempt succeeded. */
  lastRefreshFailed: boolean;
  /**
   * Whether Xero has refused calls for this organisation.
   *
   * Separate from the refresh having failed, because the two are independent:
   * this is the case where renewal works perfectly and the connection is
   * nevertheless dead.
   */
  connectionRejected?: boolean;
}

/**
 * Connection health for one client organisation.
 *
 * REAUTH means only the client can fix it by re-authorising — either the
 * 60-day window closed, or they withdrew this application's access. FAILED
 * means the window is still open but the last refresh errored, which is
 * usually recoverable on retry.
 *
 * The rejection is checked first. A connection the client has withdrawn is
 * not "expiring in 40 days"; it is already gone, and saying anything softer
 * invites somebody to wait for it to come back.
 */
export function deriveConnectionState(input: ConnectionHealthInput): ConnectionState {
  if (input.connectionRejected) return 'REAUTH';

  const remaining = REFRESH_TOKEN_WINDOW_DAYS - input.ageDays;
  if (remaining <= 0) return 'REAUTH';
  if (input.lastRefreshFailed) return 'FAILED';
  if (remaining <= EXPIRY_WARNING_DAYS) return 'EXPIRING';
  return 'OK';
}

export function daysRemaining(ageDays: number): number {
  return REFRESH_TOKEN_WINDOW_DAYS - ageDays;
}

export function needsAttention(state: ConnectionState): boolean {
  return state !== 'OK';
}
